import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Copy, Check, RefreshCw, Eye, EyeOff } from 'lucide-react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { ConfirmDialog } from '../ui/ConfirmDialog'
import { api, extractApiError } from '../../lib/api'
import { leadIngestConfig } from '../../lib/types'
import { copyText } from '../../lib/clipboard'

const SOURCE_LABEL = { meta: 'Meta', google: 'Google', webhook: 'Webhook', n8n: 'N8N' }
const SOURCE_HINT = {
  meta: 'Point your Meta (Facebook) Lead Ads webhook — or a bridge such as Zapier/Make — at this URL.',
  google: 'Send Google Lead Form submissions here via a webhook action.',
  webhook: 'POST any custom lead to this URL as JSON.',
  n8n: 'Use an N8N HTTP Request node (POST) targeting this URL.',
}
// Order the cards to match the requested sources.
const SOURCE_ORDER = ['meta', 'google', 'webhook', 'n8n']

/** Copy `text` to the clipboard, remembering WHICH field was copied so that
 *  field's button keeps reading "Copied". Routed through the shared helper
 *  for the execCommand fallback. See lib/clipboard.js. */
function useCopy() {
  const [copiedKey, setCopiedKey] = useState(null)
  async function copy(text, key) {
    if (!(await copyText(text ?? ''))) {
      toast.error('Could not copy. Please copy it manually.')
      return
    }
    setCopiedKey(key)
    toast.success('Copied to clipboard.')
  }
  return { copiedKey, copy }
}

function CopyButton({ onClick, copied }) {
  return (
    <Button variant="secondary" onClick={onClick} aria-label="Copy">
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? 'Copied' : 'Copy'}
    </Button>
  )
}

/**
 * Master Admin → Leads → Integrations. Surfaces the inbound lead-ingestion
 * config so an operator can wire Meta / Google / N8N / a custom webhook:
 * the per-source endpoint URLs, the shared secret (reveal + copy + rotate),
 * and an on/off switch. Read-only apart from rotate + toggle.
 */
export function LeadIntegrationsModal({ open, onClose }) {
  const qc = useQueryClient()
  const { copiedKey, copy } = useCopy()
  const [revealed, setRevealed] = useState(false)
  const [confirmRotate, setConfirmRotate] = useState(false)

  const cfgQuery = useQuery({
    queryKey: ['lead-ingest-config'],
    queryFn: async () => {
      const res = await api.get('/super-admin/leads/ingest-config')
      return leadIngestConfig.parse(res.data)
    },
    enabled: open,
  })

  const toggleMutation = useMutation({
    mutationFn: async (enabled) => {
      const res = await api.patch('/super-admin/leads/ingest-config', { enabled })
      return leadIngestConfig.parse(res.data)
    },
    onSuccess: (data) => {
      qc.setQueryData(['lead-ingest-config'], data)
      toast.success(data.enabled ? 'Ingestion enabled.' : 'Ingestion disabled.')
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const rotateMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/super-admin/leads/ingest-config/rotate')
      return leadIngestConfig.parse(res.data)
    },
    onSuccess: (data) => {
      qc.setQueryData(['lead-ingest-config'], data)
      setConfirmRotate(false)
      setRevealed(true)
      toast.success('A new token was generated. Update your integrations.')
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const cfg = cfgQuery.data ?? null
  const maskedToken = cfg
    ? `${cfg.token.slice(0, 8)}${'•'.repeat(12)}${cfg.token.slice(-4)}`
    : ''

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Lead Integrations"
      description="Capture leads from Meta, Google, an N8N workflow, or any custom webhook."
      size="lg"
      footer={<Button onClick={onClose}>Done</Button>}
    >
      {cfgQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
      ) : cfgQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(cfgQuery.error).message}
        </p>
      ) : cfg ? (
        <div className="space-y-5">
          {/* Enabled state + toggle */}
          <div className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                Inbound ingestion
              </p>
              <p className="text-xs text-[color:var(--color-ink-muted)]">
                {cfg.enabled
                  ? 'Active — incoming leads are being accepted.'
                  : 'Paused — all incoming requests are rejected.'}
              </p>
            </div>
            <Button
              variant={cfg.enabled ? 'secondary' : 'primary'}
              onClick={() => toggleMutation.mutate(!cfg.enabled)}
              disabled={toggleMutation.isPending}
            >
              {cfg.enabled ? 'Disable' : 'Enable'}
            </Button>
          </div>

          {/* Shared secret */}
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1.5">
              Ingestion token
            </p>
            <div className="flex items-stretch gap-2">
              <input
                readOnly
                value={revealed ? cfg.token : maskedToken}
                onFocus={(e) => e.target.select()}
                className="w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm font-mono text-[color:var(--color-ink)] outline-none"
              />
              <Button
                variant="secondary"
                onClick={() => setRevealed((v) => !v)}
                aria-label={revealed ? 'Hide Token' : 'Reveal Token'}
              >
                {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
                {revealed ? 'Hide' : 'Reveal'}
              </Button>
              <CopyButton onClick={() => copy(cfg.token, 'token')} copied={copiedKey === 'token'} />
            </div>
            <div className="mt-2 flex items-center justify-between gap-3">
              <p className="text-xs text-[color:var(--color-ink-muted)]">
                Send it as a header: <code className="font-mono">Authorization: Bearer &lt;token&gt;</code>{' '}
                or <code className="font-mono">X-Lead-Ingest-Token: &lt;token&gt;</code>.
              </p>
              <button
                type="button"
                onClick={() => setConfirmRotate(true)}
                className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--color-accent-strong)] hover:underline cursor-pointer"
              >
                <RefreshCw size={12} /> Rotate
              </button>
            </div>
          </div>

          {/* Per-source endpoints */}
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2">
              Endpoint URLs
            </p>
            <div className="space-y-2.5">
              {SOURCE_ORDER.map((s) => {
                const url = cfg.endpoints[s]
                if (!url) return null
                return (
                  <div
                    key={s}
                    className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                        {SOURCE_LABEL[s]}
                      </span>
                      <CopyButton onClick={() => copy(url, s)} copied={copiedKey === s} />
                    </div>
                    <code className="mt-1 block font-mono text-xs text-[color:var(--color-ink)] break-all">
                      POST {url}
                    </code>
                    <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                      {SOURCE_HINT[s]}
                    </p>
                  </div>
                )
              })}
            </div>
            <p className="mt-3 text-xs text-[color:var(--color-ink-muted)]">
              Post JSON with at least <code className="font-mono">fullName</code> and{' '}
              <code className="font-mono">emailId</code>. Common aliases (name, email, phone,
              company, message) are accepted automatically. Website enquiries continue to arrive
              through the public contact form.
            </p>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmRotate}
        title="Rotate Ingestion Token"
        message="Generate a new token? The current one stops working immediately — every connected integration must be updated with the new token."
        confirmLabel="Rotate Token"
        variant="danger"
        confirming={rotateMutation.isPending}
        onCancel={() => setConfirmRotate(false)}
        onConfirm={() => rotateMutation.mutate()}
      />
    </Modal>
  )
}
